<template>
    <div class="range-buttons">
        <ul class="range-buttons-list">
            <li
                v-for="(option, key) in options"
                :key="key"
                class="option"
            >
                <button
                    :class="getButtonClasses(option.value === current)"
                    @click="() => handleButtonClick(option.value)"
                >
                    {{ option.label }}
                </button>
            </li>
            <li class="option">
                <button
                    :class="getCalendarClasses(isCalendarVisible || ! isEmpty(calendarRange))"
                    @click="toggleCalendar"
                >
                    <i class="far fa-calendar-alt" />
                </button>
            </li>
        </ul>
        <date-time-picker
            v-if="isCalendarVisible"
            v-model="calendarRange"
            v-on-clickaway="toggleCalendar"
            mode="range"
            :max-date="new Date()"
            @change="toggleCalendar"
        />
    </div>
</template>

<script>

/**
 * External dependencies
 */
import { find, isEmpty, orderBy } from 'lodash';
import { mixin as clickaway } from 'vue-clickaway';
import moment from 'moment';

/**
 * Internal dependencies
 */
import DateTimePicker from '@/components/Controls/DateTimePicker';

export default {
    components: {
        DateTimePicker,
    },

    mixins: [
        clickaway,
    ],

    props: {
        buckets: {
            type: Object,
            default: () => ({
                day: 31,
                week: 140,
                month: null,
            }),
        },
    },

    data: () => ({
        calendarRange: {},
        current: 'year',
        range: {},
        isCalendarVisible: false,
        ranges: {
            '30-days': {
                label: 'Last 30 days',
                start: [30, 'days'],
            },
            '3-months': {
                label: 'Last 3 months',
                timeUnit: 'week',
                start: [3, 'months'],
            },
            year: {
                label: 'Last year',
                start: [1, 'year'],
            },
            all: {
                label: 'All Time',
                start: null,
            },
        },
        value: null,
    }),

    computed: {
        /**
         * Returns real value.
         *
         * @returns {string} Value.
         */
        realValue() {
            return this.value || this.current;
        },

        /**
         * Returns time range options prepared for RangeButtons component.
         *
         * @returns {Array} Time range options.
         */
        options() {
            const options = Object.entries(this.ranges).map(([key, item]) => ({
                value: key,
                label: item.label,
            }));

            return options;
        },

    },

    watch: {
        /**
         * Sets range on calendar selection change.
         *
         * @param {object} range Time range.
         * @returns {void}
         */
        calendarRange(range) {
            if (range) {
                this.range = range;
                this.current = null;
            }
        },

        current: {
            /**
             * Handles current value change.
             *
             * @returns {void}
             */
            handler() {
                if (! this.ranges[this.current]) {
                    return;
                }

                const data = this.ranges[this.current];

                this.range = {
                    start: data.start ? moment().subtract(...data.start).startOf('day') : null,
                    end: null,
                };
            },

            immediate: true,
        },

        range: {
            /**
             * Sets range on calendar selection change.
             *
             * @param {object} range Time range.
             * @returns {void}
             */
            handler(range) {
                const buckets = orderBy(
                    Object.keys(this.buckets).map(name => ({ name, maxDays: this.buckets[name] })),
                    'value',
                    'asc'
                );

                const startDate = range.start ? moment(range.start) : null;
                const endDate = moment(range.end || undefined);
                const diff = endDate.diff(startDate, 'days');
                const bucket = startDate
                    ? find(buckets, bucket => diff < bucket.maxDays || null === bucket.maxDays)?.name
                    : 'month';

                this.$emit('change', {
                    from: startDate ? startDate.format('YYYY-MM-DD') : null,
                    to: range.end ? endDate.format('YYYY-MM-DD') : null,
                    bucket,
                });
            },

            immediate: true,
        },
    },

    methods: {
        isEmpty,

        /**
         * Handles change.
         *
         * @param {string} value New value.
         * @returns {void}
         */
        handleButtonClick(value) {
            this.current = value;
            this.calendarRange = null;
        },

        /**
         * Toggles calendar visibility.
         *
         * @returns {void}
         */
        toggleCalendar() {
            this.isCalendarVisible = ! this.isCalendarVisible;
        },

        /**
         * Returns button classes.
         *
         * @param  {boolean} current Whether the item is current.
         * @returns {Array} Button classes.
         */
        getButtonClasses(current) {
            return [
                'btn',
                'btn-secondary',
                'is-xsmall',
                {
                    active: current,
                },
            ];
        },

        /**
         * Returns calendar button classes.
         *
         * @param  {boolean} current Whether the item is current.
         * @returns {Array} Button classes.
         */
        getCalendarClasses(current) {
            return [
                'calendar',
                ...this.getButtonClasses(current),
            ];
        },
    },
};
</script>
